import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Hash, Users, Search, Send, AlertTriangle, LogOut, Radio, Circle, ShieldCheck, MessageSquare, Bell, Paperclip, FileText, Loader2, Download, Images, ListChecks, Check, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_META = {
  disponible: { label: "Disponible", cls: "text-emerald-600", dot: "fill-emerald-500 text-emerald-500" },
  de_garde: { label: "De garde", cls: "text-sky-600", dot: "fill-sky-500 text-sky-500" },
  occupe: { label: "Occupé", cls: "text-amber-600", dot: "fill-amber-500 text-amber-500" },
  absent: { label: "Absent", cls: "text-slate-400", dot: "fill-slate-400 text-slate-400" },
};

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function IntranetSpace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [dir, setDir] = useState([]);
  const [dirQ, setDirQ] = useState("");
  const [tab, setTab] = useState("channels");
  const [active, setActive] = useState(null); // {kind:'channel'|'dm', id, title, sub}
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [codeRouge, setCodeRouge] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searchSecteur, setSearchSecteur] = useState("");
  const [searchAttach, setSearchAttach] = useState(false);
  const [status, setStatus] = useState("disponible");
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imgUrls, setImgUrls] = useState({});
  const [esc, setEsc] = useState(null); // {open, loading, data}
  const [gallery, setGallery] = useState(null); // {open, loading, items}
  const [galImgs, setGalImgs] = useState({});
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const prevUnread = useRef(0);
  const prevAlerts = useRef(null);
  const audioCtxRef = useRef(null);

  const beep = useCallback((urgent = false) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctxA = audioCtxRef.current;
      const play = (freq, start, dur) => {
        const o = ctxA.createOscillator(); const g = ctxA.createGain();
        o.frequency.value = freq; o.type = "sine"; o.connect(g); g.connect(ctxA.destination);
        g.gain.setValueAtTime(0.001, ctxA.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.25, ctxA.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctxA.currentTime + start + dur);
        o.start(ctxA.currentTime + start); o.stop(ctxA.currentTime + start + dur);
      };
      if (urgent) { play(880, 0, 0.18); play(1046, 0.22, 0.22); }
      else play(660, 0, 0.16);
    } catch { /* silencieux */ }
  }, []);

  const loadCtx = useCallback(async () => {
    const { data } = await api.get("/intranet/me/context");
    setCtx(data); setStatus(data.status);
  }, []);
  const loadChannels = useCallback(async () => { try { const { data } = await api.get("/intranet/channels"); setChannels(data.channels); } catch {} }, []);
  const loadDms = useCallback(async () => { try { const { data } = await api.get("/intranet/dm"); setDms(data.conversations); } catch {} }, []);
  const loadAlerts = useCallback(async () => { try { const { data } = await api.get("/intranet/alerts"); setAlerts(data.alerts); } catch {} }, []);
  const loadDir = useCallback(async (query = "") => { try { const { data } = await api.get(`/intranet/directory?q=${encodeURIComponent(query)}`); setDir(data.professionnels); } catch {} }, []);

  useEffect(() => { loadCtx().catch((e) => toast.error(apiError(e))); loadChannels(); loadDms(); loadAlerts(); }, [loadCtx, loadChannels, loadDms, loadAlerts]);
  useEffect(() => { if (tab === "directory") loadDir(dirQ); }, [tab, dirQ, loadDir]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openThread = useCallback(async (kind, id, title, sub) => {
    setActive({ kind, id, title, sub }); setResults(null); setQ("");
    const url = kind === "channel" ? `/intranet/channels/${id}/messages` : `/intranet/dm/${id}/messages`;
    try { const { data } = await api.get(url); setMessages(data.messages); loadChannels(); loadDms(); }
    catch (e) { toast.error(apiError(e)); }
  }, [loadChannels, loadDms]);

  // polling active thread + alerts
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      loadAlerts();
      if (!active) return;
      const url = active.kind === "channel" ? `/intranet/channels/${active.id}/messages` : `/intranet/dm/${active.id}/messages`;
      try { const { data } = await api.get(url); setMessages(data.messages); } catch {}
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [active, loadAlerts]);

  // Notifications globales (cloche + son) — nouveaux messages & code rouge, sur toute la messagerie
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const [{ data: u }, { data: a }] = await Promise.all([
          api.get("/intranet/unread"), api.get("/intranet/alerts"),
        ]);
        if (stop) return;
        setUnread(u.unread);
        setAlerts(a.alerts);
        const notif = (title, opts) => {
          try { if (document.hidden && window.Notification && Notification.permission === "granted") new Notification(title, opts); } catch { /* ignore */ }
        };
        if (u.unread > prevUnread.current) { beep(false); notif("Nouveau message — Messagerie intranet", { body: "Vous avez de nouveaux messages non lus." }); }
        prevUnread.current = u.unread;
        const ids = new Set(a.alerts.map((x) => x.id));
        if (prevAlerts.current !== null) {
          const fresh = a.alerts.filter((x) => !prevAlerts.current.has(x.id));
          if (fresh.length) {
            beep(true);
            toast.error(`Code rouge · ${fresh[0].location} — ${fresh[0].body}`, { duration: 8000 });
            notif(`🚨 CODE ROUGE · ${fresh[0].location}`, { body: `${fresh[0].sender_name} : ${fresh[0].body}`, requireInteraction: true });
          }
        }
        prevAlerts.current = ids;
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 8000);
    return () => { stop = true; clearInterval(iv); };
  }, [beep]);

  // Vignettes des pièces jointes image (récupérées en blob, cookies gérés par axios)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const m of messages) {
        const a = m.attachment;
        if (a && a.mime && a.mime.startsWith("image/") && !imgUrls[m.id]) {
          try {
            const res = await api.get(`/intranet/attachments/${m.id}`, { responseType: "blob" });
            if (cancelled) return;
            const url = URL.createObjectURL(res.data);
            setImgUrls((prev) => (prev[m.id] ? prev : { ...prev, [m.id]: url }));
          } catch { /* ignore */ }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAttachment = async (mid) => {
    try {
      const res = await api.get(`/intranet/attachments/${mid}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEscalation = async (mid) => {
    setEsc({ open: true, loading: true, data: null });
    try { const { data } = await api.get(`/intranet/messages/${mid}/escalation`); setEsc({ open: true, loading: false, data }); }
    catch (e) { toast.error(apiError(e)); setEsc(null); }
  };

  const openGallery = async () => {
    if (!active) return;
    setGallery({ open: true, loading: true, items: [] }); setGalImgs({});
    try {
      const { data } = await api.get(`/intranet/media?ref_id=${encodeURIComponent(active.id)}`);
      setGallery({ open: true, loading: false, items: data.media });
      for (const it of data.media) {
        if (it.mime.startsWith("image/")) {
          try { const r = await api.get(`/intranet/attachments/${it.id}`, { responseType: "blob" }); setGalImgs((p) => ({ ...p, [it.id]: URL.createObjectURL(r.data) })); } catch { /* ignore */ }
        }
      }
    } catch (e) { toast.error(apiError(e)); setGallery(null); }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!f || !active) return;
    const url = active.kind === "channel" ? `/intranet/channels/${active.id}/attachments` : `/intranet/dm/${active.id}/attachments`;
    const fd = new FormData(); fd.append("file", f); if (codeRouge) fd.append("code_rouge", "1");
    setUploading(true);
    try {
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, data]); setCodeRouge(false); loadChannels(); loadDms(); if (data.code_rouge) loadAlerts();
    } catch (err) { toast.error(apiError(err, "Envoi du fichier impossible")); } finally { setUploading(false); }
  };

  const send = async () => {
    if (!body.trim() || !active) return;
    const url = active.kind === "channel" ? `/intranet/channels/${active.id}/messages` : `/intranet/dm/${active.id}/messages`;
    try {
      const { data } = await api.post(url, { body, code_rouge: codeRouge });
      setMessages((m) => [...m, data]); setBody(""); setCodeRouge(false);
      loadChannels(); loadDms(); if (data.code_rouge) loadAlerts();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startDm = async (peerId, name, poste) => {
    try {
      const { data } = await api.post("/intranet/dm/start", { peer_user_id: peerId });
      await loadDms(); setTab("dms");
      openThread("dm", data.id, name, poste);
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStatus = async (st) => {
    setStatus(st);
    try { await api.put("/intranet/me/availability", { status: st }); } catch (e) { toast.error(apiError(e)); }
  };

  const runSearch = async (opts = {}) => {
    const sec = opts.secteur !== undefined ? opts.secteur : searchSecteur;
    const att = opts.attach !== undefined ? opts.attach : searchAttach;
    if (q.trim().length < 2) { setResults(null); return; }
    const params = new URLSearchParams({ q });
    if (sec) params.set("secteur", sec);
    if (att) params.set("attachments_only", "1");
    try { const { data } = await api.get(`/intranet/search?${params.toString()}`); setResults(data.results); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/auth", { replace: true }); };

  if (user && user.role && user.role !== "professionnel") { navigate("/", { replace: true }); return null; }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="intranet-space">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-navy text-white">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <ShieldCheck size={20} className="text-ciel" />
          <div>
            <p className="font-display text-sm font-bold leading-tight">Messagerie intranet</p>
            <p className="text-[11px] text-slate-300">Cabinet CARIS Santé — professionnels de santé</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="relative cursor-pointer" data-testid="intranet-bell" title="Activer les notifications du navigateur"
              onClick={() => { try { window.Notification && Notification.requestPermission().then(() => toast.success("Notifications activées")); } catch { /* ignore */ } }}>
              <Bell size={19} className={unread > 0 ? "text-ciel" : "text-slate-300"} />
              {unread > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="intranet-unread-count">{unread}</span>}
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <span className="text-xs text-slate-300">{ctx?.name}{ctx?.poste ? ` · ${ctx.poste}` : ""}</span>
              <Select value={status} onValueChange={changeStatus}>
                <SelectTrigger className="h-8 w-36 border-white/20 bg-white/10 text-xs text-white" data-testid="intranet-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}><span className="flex items-center gap-2"><Circle size={9} className={v.dot} /> {v.label}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="ghost" onClick={doLogout} data-testid="intranet-logout" className="text-white hover:bg-white/10">
              <LogOut size={15} className="mr-1" /> Quitter
            </Button>
          </div>
        </div>
      </header>

      {/* Code rouge alerts */}
      {alerts.length > 0 && (
        <div className="border-b border-red-200 bg-red-50" data-testid="intranet-alerts">
          <div className="mx-auto max-w-7xl px-4 py-2">
            {alerts.slice(0, 2).map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-sm text-red-700">
                <AlertTriangle size={15} className="animate-pulse" />
                <span className="font-semibold">Code rouge · {a.location} · {a.sender_name}</span>
                <span className="truncate text-red-600">— {a.body}</span>
                <span className="ml-auto whitespace-nowrap text-[11px] text-red-400">{fmtTime(a.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-7xl gap-4 px-4 py-4 md:grid-cols-[300px_1fr]">
        {/* Sidebar */}
        <aside className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex border-b border-slate-100 text-sm">
            {[["channels", "Canaux", Hash], ["dms", "Messages", MessageSquare], ["directory", "Répertoire", Users]].map(([k, l, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`intranet-tab-${k}`}
                className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 font-medium transition-colors ${tab === k ? "border-b-2 border-ciel text-navy" : "text-slate-400 hover:text-slate-600"}`}>
                <Icon size={14} /> {l}
              </button>
            ))}
          </div>
          <div className="max-h-[62vh] overflow-y-auto p-2">
            {tab === "channels" && channels.map((c) => (
              <button key={c.id} onClick={() => openThread("channel", c.id, c.name, c.type === "general" ? "Tous les professionnels" : "Canal secteur")}
                data-testid={`intranet-channel-${c.id}`}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm ${active?.id === c.id ? "bg-ciel/10 text-navy" : "hover:bg-slate-50 text-slate-600"}`}>
                <Hash size={15} className="text-slate-400" />
                <span className="flex-1 font-medium">{c.name}</span>
                {c.unread > 0 && <span className="rounded-full bg-ciel px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
              </button>
            ))}
            {tab === "dms" && (dms.length === 0 ? <p className="p-3 text-xs text-slate-400">Aucune conversation. Ouvrez le répertoire pour démarrer un échange.</p> :
              dms.map((c) => {
                const sm = STATUS_META[c.peer.status] || STATUS_META.disponible;
                return (
                  <button key={c.id} onClick={() => openThread("dm", c.id, c.peer.name, c.peer.poste)}
                    data-testid={`intranet-dm-${c.peer.user_id}`}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm ${active?.id === c.id ? "bg-ciel/10 text-navy" : "hover:bg-slate-50 text-slate-600"}`}>
                    <Circle size={9} className={sm.dot} />
                    <span className="flex-1"><span className="font-medium">{c.peer.name}</span><span className="block text-[11px] text-slate-400">{c.peer.poste}</span></span>
                    {c.unread > 0 && <span className="rounded-full bg-ciel px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
                  </button>
                );
              }))}
            {tab === "directory" && (
              <div>
                <div className="p-2"><Input value={dirQ} onChange={(e) => setDirQ(e.target.value)} placeholder="Rechercher un collègue…" className="h-9 text-sm" data-testid="intranet-directory-search" /></div>
                {dir.map((p) => {
                  const sm = STATUS_META[p.status] || STATUS_META.disponible;
                  return (
                    <div key={p.user_id} className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm hover:bg-slate-50" data-testid={`intranet-dir-${p.user_id}`}>
                      <Circle size={9} className={sm.dot} />
                      <span className="flex-1"><span className="font-medium text-slate-700">{p.name}</span><span className="block text-[11px] text-slate-400">{p.poste} · {p.secteur}</span></span>
                      <Button size="sm" variant="outline" onClick={() => startDm(p.user_id, p.name, p.poste)} data-testid={`intranet-dm-start-${p.user_id}`} className="h-7 rounded-full text-[11px]">Message</Button>
                    </div>
                  );
                })}
                {dir.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun professionnel trouvé.</p>}
              </div>
            )}
          </div>
        </aside>

        {/* Thread */}
        <main className="flex min-h-[70vh] flex-col rounded-2xl border border-slate-200 bg-white">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center text-slate-400">
              <Radio size={40} className="text-slate-200" />
              <p className="text-sm">Sélectionnez un canal ou une conversation pour commencer.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
                <div className="flex-1">
                  <p className="font-display font-semibold text-navy" data-testid="intranet-thread-title">{active.kind === "channel" ? `# ${active.title}` : active.title}</p>
                  {active.sub && <p className="text-[11px] text-slate-400">{active.sub}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={openGallery} data-testid="intranet-gallery-btn" className="h-8 rounded-full text-xs">
                    <Images size={13} className="mr-1" /> Galerie
                  </Button>
                  <div className="flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1">
                    <Search size={13} className="text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} onBlur={runSearch}
                      placeholder="Rechercher…" className="w-32 bg-transparent text-xs outline-none" data-testid="intranet-search" />
                  </div>
                </div>
              </div>

              {results !== null ? (
                <div className="flex-1 overflow-y-auto p-4" data-testid="intranet-search-results">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <p className="text-xs font-semibold text-slate-500">{results.length} résultat(s) pour « {q} »</p>
                    <div className="ml-auto flex items-center gap-2">
                      <select value={searchSecteur} onChange={(e) => { setSearchSecteur(e.target.value); runSearch({ secteur: e.target.value }); }}
                        data-testid="intranet-search-secteur" className="rounded-full border border-slate-200 px-2 py-1 text-[11px] outline-none">
                        <option value="">Tous les canaux</option>
                        {ctx?.secteur && <option value={ctx.secteur}>Secteur : {ctx.secteur}</option>}
                      </select>
                      <label className="flex items-center gap-1 text-[11px] text-slate-500">
                        <input type="checkbox" checked={searchAttach} onChange={(e) => { setSearchAttach(e.target.checked); runSearch({ attach: e.target.checked }); }} data-testid="intranet-search-attach" />
                        Pièces jointes uniquement
                      </label>
                    </div>
                  </div>
                  {results.map((r) => (
                    <div key={r.id} className="mb-2 rounded-lg border border-slate-100 p-3 text-sm">
                      <p className="flex items-center gap-2 text-[11px] text-slate-400">
                        {r.location} · {r.sender_name} · {fmtTime(r.created_at)}
                        {r.matched === "piece_jointe" && <span className="rounded-full bg-ciel/10 px-1.5 text-[10px] font-semibold text-ciel">pièce jointe</span>}
                      </p>
                      {r.body && <p className="text-slate-700">{r.body}</p>}
                      {r.attachment && (
                        <button onClick={() => openAttachment(r.id)} data-testid={`intranet-search-attach-${r.id}`} className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs hover:bg-slate-50">
                          <FileText size={14} /><span className="flex-1 truncate">{r.attachment.name}</span><Download size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => { setResults(null); setQ(""); setSearchSecteur(""); setSearchAttach(false); }} className="text-xs">← Retour à la conversation</Button>
                </div>
              ) : (
                <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="intranet-messages">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${m.code_rouge ? "border border-red-300 bg-red-50" : m.mine ? "bg-navy text-white" : "bg-slate-100 text-slate-700"}`}>
                        {!m.mine && <p className="mb-0.5 text-[11px] font-semibold text-ciel">{m.sender_name}</p>}
                        {m.code_rouge && <p className="mb-1 flex items-center gap-1 text-[11px] font-bold text-red-600"><AlertTriangle size={11} /> CODE ROUGE</p>}
                        {m.body && <p className={m.code_rouge ? "text-red-800" : ""}>{m.body}</p>}
                        {m.attachment && (
                          m.attachment.mime.startsWith("image/") ? (
                            <button onClick={() => openAttachment(m.id)} data-testid={`intranet-attach-${m.id}`} className="mt-1 block overflow-hidden rounded-lg">
                              {imgUrls[m.id]
                                ? <img src={imgUrls[m.id]} alt={m.attachment.name} className="max-h-52 max-w-full rounded-lg object-cover" />
                                : <span className="flex items-center gap-2 rounded-lg bg-black/10 px-3 py-4 text-xs"><Loader2 size={14} className="animate-spin" /> Chargement de l'image…</span>}
                              <span className={`mt-0.5 block text-[10px] ${m.mine ? "text-slate-300" : "text-slate-400"}`}>{m.attachment.name} · {Math.round(m.attachment.size / 1024)} Ko</span>
                            </button>
                          ) : (
                            <button onClick={() => openAttachment(m.id)} data-testid={`intranet-attach-${m.id}`}
                              className={`mt-1 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs ${m.mine ? "bg-white/15 hover:bg-white/25" : "bg-white hover:bg-slate-50 border border-slate-200"}`}>
                              <FileText size={15} />
                              <span className="flex-1 truncate">{m.attachment.name}</span>
                              <span className="opacity-70">{Math.round(m.attachment.size / 1024)} Ko</span>
                              <Download size={13} />
                            </button>
                          )
                        )}
                        <p className={`mt-1 text-[10px] ${m.mine ? "text-slate-300" : "text-slate-400"}`}>
                          {fmtTime(m.created_at)}{m.mine && m.read_by.length > 0 ? " · Lu ✓✓" : m.mine ? " · Envoyé" : ""}
                        </p>
                        {m.mine && m.code_rouge && (
                          <button onClick={() => openEscalation(m.id)} data-testid={`intranet-esc-followup-${m.id}`}
                            className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-red-500 hover:underline">
                            <ListChecks size={11} /> Suivi de l'escalade
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
              )}

              <div className="border-t border-slate-100 p-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setCodeRouge((v) => !v)} data-testid="intranet-code-rouge"
                    title="Marquer comme code rouge (urgent)"
                    className={`flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${codeRouge ? "border-red-500 bg-red-500 text-white" : "border-slate-200 text-slate-500 hover:border-red-300"}`}>
                    <AlertTriangle size={13} /> Code rouge
                  </button>
                  <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="intranet-file-input" />
                  <button onClick={onPickFile} disabled={uploading} data-testid="intranet-attach-btn"
                    title="Joindre un document (PDF ou image, chiffré)"
                    className="flex items-center rounded-full border border-slate-200 p-2 text-slate-500 hover:border-ciel hover:text-ciel disabled:opacity-50">
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
                  </button>
                  <Input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                    placeholder="Écrire un message…" className="h-10 flex-1" data-testid="intranet-input" />
                  <Button onClick={send} disabled={!body.trim()} data-testid="intranet-send" className="h-10 rounded-full bg-ciel px-4 text-white hover:bg-sky-600">
                    <Send size={16} />
                  </Button>
                </div>
                <p className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-400"><ShieldCheck size={11} /> Messages chiffrés — inaccessibles à l'administration.</p>
              </div>
            </>
          )}
        </main>
      </div>

      {/* Dialog suivi escalade */}
      <Dialog open={!!esc?.open} onOpenChange={(o) => !o && setEsc(null)}>
        <DialogContent className="max-w-lg" data-testid="intranet-esc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-navy"><ListChecks size={18} className="text-red-500" /> Suivi de l'escalade — code rouge</DialogTitle>
          </DialogHeader>
          {esc?.loading ? <p className="text-sm text-slate-400"><Loader2 size={14} className="mr-1 inline animate-spin" /> Chargement…</p> : esc?.data && (
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {esc.data.resolved
                  ? <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="intranet-esc-resolved">✓ Résolu — lu par un soignant de garde</span>
                  : <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">En cours — non lu</span>}
                {esc.data.relances > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] text-slate-600" data-testid="intranet-esc-relances">Relance n°{esc.data.relances}</span>}
              </div>
              {esc.data.escalated
                ? <p className="mb-2 text-xs text-slate-500">Dernière escalade le {fmtTime(esc.data.escalated_at)} · {esc.data.count} soignant(s) « De garde » alerté(s).</p>
                : <p className="text-sm text-slate-500">Ce message n'a pas encore été escaladé (délai non atteint ou déjà lu).</p>}
              <div className="space-y-2">
                {esc.data.recipients.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid="intranet-esc-recipient">
                    <span className="flex-1 font-medium text-slate-700">{r.name}</span>
                    <span className={`flex items-center gap-1 text-[11px] ${r.delivered ? "text-emerald-600" : "text-slate-400"}`}>
                      {r.delivered ? <Check size={12} /> : <X size={12} />} SMS/WhatsApp {r.mode === "simulé" ? "(simulé)" : r.delivered ? "délivré" : (r.mode || "—")}
                    </span>
                    <span className={`flex items-center gap-1 text-[11px] ${r.lu_app ? "text-emerald-600" : "text-amber-500"}`}>
                      {r.lu_app ? <Check size={12} /> : <Circle size={9} />} {r.lu_app ? "Lu dans l'app" : "Non lu"}
                    </span>
                  </div>
                ))}
                {esc.data.recipients.length === 0 && <p className="text-sm text-slate-400">Aucun destinataire (aucun soignant « De garde » à alerter).</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog galerie */}
      <Dialog open={!!gallery?.open} onOpenChange={(o) => !o && setGallery(null)}>
        <DialogContent className="max-w-3xl" data-testid="intranet-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-navy"><Images size={18} className="text-ciel" /> Galerie — {active?.kind === "channel" ? `# ${active?.title}` : active?.title}</DialogTitle>
          </DialogHeader>
          {gallery?.loading ? <p className="text-sm text-slate-400"><Loader2 size={14} className="mr-1 inline animate-spin" /> Chargement…</p> : (
            <div className="max-h-[60vh] overflow-y-auto">
              {gallery?.items?.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Aucun document partagé dans cette conversation.</p>}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {gallery?.items?.map((it) => (
                  <button key={it.id} onClick={() => openAttachment(it.id)} data-testid={`intranet-gallery-item-${it.id}`}
                    className="flex flex-col overflow-hidden rounded-lg border border-slate-200 text-left hover:border-ciel">
                    <div className="flex h-28 items-center justify-center bg-slate-50">
                      {it.mime.startsWith("image/") ? (galImgs[it.id] ? <img src={galImgs[it.id]} alt={it.name} className="h-full w-full object-cover" /> : <Loader2 size={16} className="animate-spin text-slate-300" />) : <FileText size={30} className="text-slate-300" />}
                    </div>
                    <div className="p-2">
                      <p className="truncate text-xs font-medium text-slate-700">{it.name}</p>
                      <p className="text-[10px] text-slate-400">{it.sender_name} · {Math.round(it.size / 1024)} Ko</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
